import React, { useEffect, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { ApexOptions } from "apexcharts";
import { useQzTrayConnection } from "../../../hooks/qztray/useQzTrayConnection";

/**
 * 📊 ConnectionLatencyChart
 *
 * Muestra la evolución de la latencia (responseTimeMs) del cliente QZ Tray.
 * Actualiza los datos cada 2 segundos mediante el hook `useQzTrayConnection`.
 */
const ConnectionLatencyChart: React.FC = () => {
  const { status, fetchLiveStatus } = useQzTrayConnection();

  const [isDarkMode, setIsDarkMode] = useState<boolean>(
    document.documentElement.classList.contains("dark")
  );

  const [series, setSeries] = useState([
    {
      name: "Latencia (ms)",
      data: [] as [number, number][],
    },
  ]);

  // 🎨 Detectar modo oscuro en tiempo real
  useEffect(() => {
    const observer = new MutationObserver(() => {
      setIsDarkMode(document.documentElement.classList.contains("dark"));
    });
    observer.observe(document.documentElement, { attributes: true });
    return () => observer.disconnect();
  }, []);

  // 🔁 Obtener datos reales del hook
  useEffect(() => {
    // Primera actualización inmediata
    fetchLiveStatus();

    const interval = setInterval(() => {
      fetchLiveStatus();
    }, 2000);

    return () => clearInterval(interval);
  }, []);

  // 📈 Actualizar serie de latencia
  useEffect(() => {
    if (status?.responseTimeMs != null) {
      const now = new Date().getTime();
      setSeries((prev) => {
        const updated = [...prev[0].data, [now, status.responseTimeMs]].slice(-20);
        return [{ name: "Latencia (ms)", data: updated }];
      });
    }
  }, [status?.responseTimeMs]);

  // ⚙️ Configuración de ApexCharts
  const options: ApexOptions = {
    colors: [isDarkMode ? "#60A5FA" : "#3C50E0"],
    chart: {
      id: "latency-monitor",
      type: "area",
      height: 310,
      background: "transparent",
      toolbar: { show: false },
      zoom: { enabled: false },
      foreColor: isDarkMode ? "#CBD5E1" : "#475569",
      animations: {
        enabled: true,
        easing: "easeinout",
        speed: 500,
        dynamicAnimation: { enabled: true, speed: 500 },
      },
    },
    stroke: { curve: "smooth", width: 2 },
    fill: {
      type: "gradient",
      gradient: {
        shadeIntensity: 0.6,
        opacityFrom: 0.45,
        opacityTo: 0,
        stops: [0, 90, 100],
      },
    },
    dataLabels: { enabled: false },
    xaxis: {
      type: "datetime",
      labels: { datetimeUTC: false },
      axisBorder: { show: false },
      axisTicks: { show: false },
    },
    yaxis: {
      min: 0,
      max: 300,
      tickAmount: 6,
      labels: {
        formatter: (val) => `${val} ms`,
      },
    },
    tooltip: {
      theme: isDarkMode ? "dark" : "light",
      x: { format: "HH:mm:ss" },
      y: { formatter: (val) => `${val} ms` },
    },
    grid: {
      borderColor: isDarkMode ? "#334155" : "#E2E8F0",
      strokeDashArray: 7,
      xaxis: { lines: { show: true } },
      yaxis: { lines: { show: true } },
    },
  };

  return (
    <div className="col-span-12 rounded-sm border border-stroke bg-white px-5 pb-5 pt-7.5 shadow-default dark:border-strokedark dark:bg-boxdark sm:px-7.5 xl:col-span-7 transition-colors">
      {/* 🔹 Header */}
      <div className="mb-5.5 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h4 className="text-title-sm2 font-bold text-black dark:text-white">
            Latencia de Conexión
          </h4>
        </div>

        <div className="relative z-20 inline-block rounded">
          <select className="relative z-20 inline-flex appearance-none rounded border border-stroke bg-transparent py-[5px] pl-3 pr-8 text-sm font-medium outline-none dark:border-strokedark">
            <option value="" className="dark:bg-boxdark">
              Últimos 30 segundos
            </option>
            <option value="" className="dark:bg-boxdark">
              Últimos 60 segundos
            </option>
          </select>
          <span className="absolute right-3 top-1/2 z-10 -translate-y-1/2">
            <svg
              width="17"
              height="17"
              viewBox="0 0 17 17"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.61025 11.8872C8.46025 11.8872 8.33525 11.8372 8.21025 11.7372L2.46025 6.08723C2.23525 5.86223 2.23525 5.51223 2.46025 5.28723C2.68525 5.06223 3.03525 5.06223 3.26025 5.28723L8.61025 10.5122L13.9603 5.23723C14.1853 5.01223 14.5353 5.01223 14.7603 5.23723C14.9853 5.46223 14.9853 5.81223 14.7603 6.03723L9.01025 11.6872C8.88525 11.8122 8.76025 11.8872 8.61025 11.8872Z"
                fill="#64748B"
              />
            </svg>
          </span>
        </div>
      </div>

      {/* 🔸 Datos resumen */}
      <div className="mb-3 flex flex-wrap gap-6">
        <div>
          <p className="mb-1.5 text-sm font-medium">Promedio Actual</p>
          <p className="font-medium text-black dark:text-white">
            {series[0].data.length > 0
              ? `${Math.round(
                  series[0].data[series[0].data.length - 1][1]
                )} ms`
              : "--"}
          </p>
        </div>

        <div>
          <p className="mb-1.5 text-sm font-medium">Última Actualización</p>
          <p className="font-medium text-black dark:text-white">
            {new Date().toLocaleTimeString()}
          </p>
        </div>
      </div>

      {/* 🔹 Gráfico */}
      <div>
        <div id="chartLatency" className="-ml-5">
          <ReactApexChart
            options={options}
            series={series}
            type="area"
            height={310}
          />
        </div>
      </div>
    </div>
  );
};

export default ConnectionLatencyChart;
